// built-from: /fact-log-replica v1 impl:fact-log-replica role:implementation
// The client's LOCAL REPLICA of a facilitation session's fact event log
// (054-facilitated-vision, T041 — FR-015/FR-021, SC-007; data-model.md §7).
//
// The replica is `{ events, pending }`:
//   - `events`  — server-ordered truth: seeded by
//                 GET /api/facilitation/{sessionId}/log, appended by the done
//                 frame's `factEvents` (applyTurnEvents) and by capability-POST
//                 acknowledgements;
//   - `pending` — optimistic local entries (a client nonce each), rendered in
//                 the SAME KEYSTROKE. Acknowledgement REPLACES a pending entry
//                 with the server-ordered event(s); rejection REMOVES it, the
//                 projection reverts, and the refusal is exposed via
//                 `rejection` (the shell renders it role="alert" — never
//                 silent).
//
// `projectSurfaceFactState` is the PURE projection the shell applies (the
// materialisation gate): last event per factId wins (SET semantics over the
// ordered log), lifecycle-gated — `uncertain` only in `drafts`, `rejected` in
// neither (it leaves the surface, never the log).
//
// CROSS-SCREEN machinery (constitution III client layering): this module
// names NO fact type, capability id, or screen concept. The optimistic
// interpretation of a pending entry is derived from the capability id's
// generic verb grammar (`<ns>.<verb>_<object>`), so the next facilitated
// surface reuses the replica with zero edits here.

import { useCallback, useEffect, useState } from 'react';
import type {
  FactLifecycle,
  GatedFact,
  SurfaceEdit,
  SurfaceFactState,
} from './surfaceBinding';
import { createFacilitationId } from './commandIdentity';

/** A fact event as the store orders it (data-model.md §1; eventId/seq are server-assigned). */
export interface FactEvent {
  eventId: string;
  seq: number;
  sessionId: string;
  type: string;
  factId: string;
  content: Record<string, unknown>;
  lifecycle: FactLifecycle;
  occurredAt: string;
  confirmedAt?: string;
  provenance: Record<string, unknown>;
  attribution?: Record<string, unknown> | null;
  origin?: {
    provenance: Record<string, unknown>;
    attribution?: Record<string, unknown> | null;
  };
}

/** An optimistic local entry: the capability projection plus its client nonce. */
export interface PendingLocalEvent {
  nonce: string;
  capabilityId: string;
  input: Record<string, unknown>;
}

// ── The pure projection (the shell's materialisation gate) ──────────────────

// Only these lifecycles materialise onto the surface's `facts`; `uncertain`
// is the tray's alone and `rejected` renders nowhere (data-model.md §3).
const MATERIALISED = new Set<FactLifecycle>(['assumed', 'confirmed', 'disputed']);

function gatedFromEvent(event: FactEvent): GatedFact {
  return {
    factId: event.factId,
    type: event.type,
    content: event.content,
    lifecycle: event.lifecycle,
    provenance: event.provenance,
    ...(event.origin !== undefined ? { origin: event.origin } : {}),
    ...(event.attribution !== undefined ? { attribution: event.attribution } : {}),
    ...(event.confirmedAt !== undefined ? { confirmedAt: event.confirmedAt } : {}),
  } as GatedFact;
}

// Parse `<ns>.<verb>_<object>` — the generic capability grammar every
// facilitation bundle's registry follows (e.g. add_goal, edit_fact,
// designate_crux). No surface vocabulary is hardcoded: the namespace, verb
// and object all come from the capability id at runtime.
function parseCapabilityId(capabilityId: string): {
  ns: string;
  verb: string;
  object: string;
} {
  const dot = capabilityId.indexOf('.');
  const ns = dot === -1 ? '' : capabilityId.slice(0, dot);
  const op = dot === -1 ? capabilityId : capabilityId.slice(dot + 1);
  const us = op.indexOf('_');
  return {
    ns,
    verb: us === -1 ? op : op.slice(0, us),
    object: us === -1 ? '' : op.slice(us + 1),
  };
}

// The local sentinel provenance a provisional fact carries until the server
// orders the real event (which replaces the overlay wholesale on ack).
const LOCAL_PROVENANCE: Record<string, unknown> = Object.freeze({
  statedBy: { kind: 'shared-session' },
  recordedBy: { kind: 'shared-session' },
});

// Overlay ONE pending local entry on the folded fact map (wireframe §3 rows
// 1b/3/4/6/8/9). Unknown verbs are ignored — an uninterpretable pending entry
// simply does not project (the ack will order the truth).
function applyPendingOverlay(
  byId: Map<string, GatedFact>,
  order: string[],
  pending: PendingLocalEvent,
): void {
  const { ns, verb, object } = parseCapabilityId(pending.capabilityId);
  const input = pending.input || {};

  const upsert = (factId: string, fact: GatedFact) => {
    if (!byId.has(factId)) order.push(factId);
    byId.set(factId, fact);
  };

  if (verb === 'add' && typeof input.text === 'string') {
    // A direct add: a brand-new fact at assumed, keyed by the nonce until the
    // server assigns the real factId (§3 rows 1b/2).
    const type = `${ns}.${object}`;
    upsert(`${type}:pending-${pending.nonce}`, {
      factId: `${type}:pending-${pending.nonce}`,
      type,
      content: { ...input },
      lifecycle: 'assumed',
      provenance: LOCAL_PROVENANCE,
      provisionalLocal: true,
    });
    return;
  }

  if (verb === 'designate') {
    // A singleton designation (`<ns>.<object>`, SET semantics): the mark
    // moves in the same click (§3 row 6); a reject snaps it back.
    const factId = `${ns}.${object}`;
    upsert(factId, {
      factId,
      type: factId,
      content: { ...input },
      lifecycle: 'assumed',
      provenance: LOCAL_PROVENANCE,
      provisionalLocal: true,
    });
    return;
  }

  // The remaining verbs all target an existing fact via input.factId.
  const targetId = typeof input.factId === 'string' ? input.factId : '';
  const target = targetId ? byId.get(targetId) : undefined;
  if (!target) return;
  const { factId: _targetFactId, ...inputRest } = input;

  switch (verb) {
    case 'edit': {
      // Overlay the edited value; an edit of a confirmed fact demotes to
      // assumed (FR-018 — mirrored optimistically), a draft stays a draft.
      byId.set(targetId, {
        ...target,
        content: { ...target.content, ...inputRest },
        lifecycle: target.lifecycle === 'uncertain' ? 'uncertain' : 'assumed',
        provisionalLocal: true,
      });
      return;
    }
    case 'reconcile': {
      // The agreed wording clears the dispute back to assumed (§3 row 7).
      const { dispute: _resolvedDispute, ...standing } = target.content;
      byId.set(targetId, {
        ...target,
        content: { ...standing, ...inputRest },
        lifecycle: 'assumed',
        provisionalLocal: true,
      });
      return;
    }
    case 'confirm':
      byId.set(targetId, { ...target, lifecycle: 'confirmed', provisionalLocal: true });
      return;
    case 'reject':
      // The card leaves the surface immediately (§3 row 8); the gate below
      // drops rejected facts from both lists.
      byId.set(targetId, { ...target, lifecycle: 'rejected', provisionalLocal: true });
      return;
    case 'raise':
      // uncertain → assumed: the draft leaves the tray onto the surface (§3 row 9).
      byId.set(targetId, { ...target, lifecycle: 'assumed', provisionalLocal: true });
      return;
    default:
      // Unknown verb — no optimistic interpretation; the ack orders the truth.
      return;
  }
}

/**
 * The PURE projection over the replica: last event per factId (in server seq
 * order), pending local entries overlaid, then the materialisation gate.
 * Deterministic and non-mutating — the rendered surface state is reproducible
 * from the log alone (SC-001).
 */
export function projectSurfaceFactState(
  events: FactEvent[],
  pending: PendingLocalEvent[] = [],
): SurfaceFactState {
  const ordered = events.slice().sort((a, b) => (a.seq ?? 0) - (b.seq ?? 0));

  const order: string[] = [];
  const byId = new Map<string, GatedFact>();
  for (const event of ordered) {
    if (!event || typeof event.factId !== 'string') continue;
    if (!byId.has(event.factId)) order.push(event.factId);
    byId.set(event.factId, gatedFromEvent(event));
  }

  for (const entry of pending) applyPendingOverlay(byId, order, entry);

  const facts: GatedFact[] = [];
  const drafts: GatedFact[] = [];
  for (const factId of order) {
    const fact = byId.get(factId);
    if (!fact) continue;
    // Retired Brief proposal events remain in history but have no active UI.
    if (fact.type === 'engagement.brief.proposal') continue;
    if (fact.lifecycle === 'uncertain') drafts.push(fact);
    else if (MATERIALISED.has(fact.lifecycle)) facts.push(fact);
    // rejected: neither — off the surface, never out of the log.
  }

  // Seal state is a store ROW, not a log event; Phase E (close/seal UI)
  // threads it to the client. Until then the projection reports unsealed.
  return { facts, drafts, sealed: false };
}

// ── The replica hook ─────────────────────────────────────────────────────────

// A command may be retried after a reload, so its id must not restart with a
// module-local counter. UUIDv4 keeps the server's idempotency key unique across
// mounts, tabs and browser restarts. The fallback is only for runtimes without
// Web Crypto (older test/browser shells) and still emits a standards-shaped v4.
// Append server-ordered events, deduplicating on eventId (a done-frame replay
// of an already-acked event must not double the replica).
function mergeEvents(prev: FactEvent[], incoming: FactEvent[]): FactEvent[] {
  const fresh = (Array.isArray(incoming) ? incoming : []).filter(
    (e): e is FactEvent => Boolean(e) && typeof e === 'object',
  );
  if (fresh.length === 0) return prev;
  const seen = new Set(prev.map((e) => e.eventId));
  const added = fresh.filter((e) => !seen.has(e.eventId));
  return added.length === 0 ? prev : [...prev, ...added];
}

export interface UseFactLogReturn {
  /** The server-ordered replica. */
  events: FactEvent[];
  /** Optimistic local entries, each carrying its client nonce. */
  pending: PendingLocalEvent[];
  /**
   * POST /api/facilitation/{sessionId}/{capabilityId} with edit.input as the
   * JSON body. Optimistic: the pending entry exists synchronously. Resolves
   * `true` on acknowledgement, `false` on a server refusal (never throws) —
   * callers typed against Promise<void> simply ignore the value.
   */
  emitEdit: (edit: SurfaceEdit) => Promise<boolean>;
  /** Append a turn's done-frame factEvents to the replica (FR-021). */
  applyTurnEvents: (events: FactEvent[]) => void;
  /** The last server rejection message (the shell renders it role="alert"). */
  rejection: string | null;
  /**
   * True once the log seed has RESOLVED with a good body (056, owner-agreed
   * opening design, 2026-07-08): lets the shell distinguish "confirmed empty"
   * (a new session: fire the agent-delivered opening) from "not yet loaded"
   * or "seed failed" (fire nothing; a wrong opening on a resumed session is
   * the failure the empty-log signal exists to prevent).
   */
  seeded: boolean;
  sealed: boolean;
  completion: {
    eligible: boolean;
    outstanding: string[];
  } | null;
}

export function useFactLog(sessionId: string, enabled = true): UseFactLogReturn {
  const [events, setEvents] = useState<FactEvent[]>([]);
  const [pending, setPending] = useState<PendingLocalEvent[]>([]);
  const [rejection, setRejection] = useState<string | null>(null);
  const [seeded, setSeeded] = useState(false);
  const [sealed, setSealed] = useState(false);
  const [completion, setCompletion] = useState<UseFactLogReturn['completion']>(null);

  // Seed the replica from the session log on mount / session switch.
  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    setSeeded(false);
    (async () => {
      try {
        const res = await fetch(`/api/facilitation/${encodeURIComponent(sessionId)}/log`);
        if (!res.ok || cancelled) return;
        const body = (await res.json().catch(() => null)) as {
          events?: FactEvent[];
          seal?: { sealed?: boolean };
          completion?: UseFactLogReturn['completion'];
        } | null;
        if (!cancelled && body && Array.isArray(body.events)) {
          setEvents((prev) => mergeEvents(prev, body.events as FactEvent[]));
          setSeeded(true);
          setSealed(body.seal?.sealed === true);
          setCompletion(body.completion || null);
        }
      } catch {
        // Seed failure degrades to an empty replica; a later turn re-syncs.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [enabled, sessionId]);

  const applyTurnEvents = useCallback((turnEvents: FactEvent[]) => {
    setEvents((prev) => mergeEvents(prev, turnEvents));
    void fetch(`/api/facilitation/${encodeURIComponent(sessionId)}/log`)
      .then((response) => response.ok ? response.json() : null)
      .then((body) => {
        if (body?.completion) setCompletion(body.completion);
        if (body?.seal?.sealed === true) setSealed(true);
      })
      .catch(() => {});
  }, [sessionId]);

  const emitEdit = useCallback(
    async (edit: SurfaceEdit): Promise<boolean> => {
      const nonce = createFacilitationId();
      const entry: PendingLocalEvent = {
        nonce,
        capabilityId: edit.capabilityId,
        input: edit.input,
      };
      // Same-keystroke optimism: the pending entry is present SYNCHRONOUSLY,
      // before the POST resolves (FR-015).
      setPending((prev) => [...prev, entry]);
      setRejection(null);

      const removePending = () =>
        setPending((prev) => prev.filter((p) => p.nonce !== nonce));

      try {
        const baseEventId = events.length > 0
          ? events.slice().sort((a, b) => a.seq - b.seq)[events.length - 1]?.eventId
          : '0';
        const command = {
          schemaVersion: 1,
          commandId: nonce,
          capabilityId: edit.capabilityId,
          input: edit.input,
          baseEventId,
        };
        const res = await fetch(
          `/api/facilitation/${encodeURIComponent(sessionId)}/${encodeURIComponent(
            edit.capabilityId,
          )}`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ command }),
          },
        );
        const body = (await res.json().catch(() => null)) as
          | {
              events?: FactEvent[];
              completion?: UseFactLogReturn['completion'];
              error?: string | { message?: string };
            }
          | null;
        if (res.ok) {
          // Ack: the server-ordered event(s) REPLACE the pending entry.
          if (body && Array.isArray(body.events)) {
            setEvents((prev) => mergeEvents(prev, body.events as FactEvent[]));
          }
          if (body?.completion) setCompletion(body.completion);
          removePending();
          return true;
        }
        // Reject: remove the pending entry (the projection reverts) and
        // expose the refusal — never silent.
        removePending();
        setRejection(
          body && typeof body.error === 'string' && body.error
            ? body.error
            : body && typeof body.error === 'object' && body.error?.message
              ? body.error.message
            : `The server refused ${edit.capabilityId}`,
        );
        return false;
      } catch {
        removePending();
        setRejection('The request failed — your change was not saved.');
        return false;
      }
    },
    [events, sessionId],
  );

  return { events, pending, emitEdit, applyTurnEvents, rejection, seeded, sealed, completion };
}
